import { useEffect } from 'react';

/** CSS custom property that carries the measured scrollbar width. */
const SCROLLBAR_WIDTH_VAR = '--ws-nav-drawer-scrollbar-width';

/** Class Bootstrap uses on <body> to compensate for the removed scrollbar. */
const MODAL_OPEN_CLASS = 'modal-open';

/**
 * Locks page scroll while `isLocked` is true.
 *
 * Measures the scrollbar width lost when the page can no longer scroll and
 * exposes it via a CSS custom property on <html>, then adds `modal-open` to
 * <body> so the existing padding-compensation styles keyed off that class kick
 * in. Both are cleared when `isLocked` becomes false and on unmount.
 *
 * Idempotent alongside Bootstrap's own `modal-open` toggling: this repo only
 * ever has one of {drawer, mega menu} open at a time, so a plain boolean toggle
 * is safe.
 *
 * @param {boolean} isLocked - Whether page scroll should currently be locked.
 */
const useBodyScrollLock = (isLocked) => {
	useEffect(() => {
		if (!isLocked || typeof window === 'undefined') return undefined;

		const scrollbarWidth =
			window.innerWidth - document.documentElement.clientWidth;
		document.documentElement.style.setProperty(
			SCROLLBAR_WIDTH_VAR,
			`${scrollbarWidth}px`
		);
		document.body.classList.add(MODAL_OPEN_CLASS);

		return () => {
			document.documentElement.style.removeProperty(SCROLLBAR_WIDTH_VAR);
			document.body.classList.remove(MODAL_OPEN_CLASS);
		};
	}, [isLocked]);
};

export default useBodyScrollLock;
